import {
  applyDocumentMask,
  DecimalFieldFormat,
  formatDateTime,
  formatDecimal,
  formatTime,
  onlyDigits,
  parseTemporalValue,
  TemporalFieldFormat
} from './cpa-grid-bridge-format.utils';

/**
 * Bridge mínimo pro contexto AngularJS que `enhanceIntegerInputs` precisa consultar pra
 * sincronizar o ngModel (scope/ng mudam de instância ao longo do ciclo de vida do
 * CpaGridBridgeComponent — daí os getters em vez de valores fixos no construtor).
 */
export interface CpaFieldMaskAngularJsContext {
  getScope(): any;
  getNg(): any;
}

/**
 * Aplica as máscaras de campo (inteiro, CPF/CNPJ, temporal em texto, decimal) nos inputs
 * renderizados pelo cpa-grid legado. Bind único por input (flags `__cpaXEnhanced`), aplicação
 * imediata e nos eventos `input`/`blur`.
 */
export class CpaFieldMaskController {
  constructor(private readonly angularJs: CpaFieldMaskAngularJsContext) {}

  enhanceDateInputs(
    hostEl: HTMLElement | null | undefined,
    temporalTypeByTitle: Record<string, 'DATE' | 'TIME' | 'DATETIME'>
  ): void {
    if (!hostEl) {
      return;
    }

    const temporalInputs = hostEl.querySelectorAll('input[data-bf-date], input[bf-date], .template-field input');
    temporalInputs.forEach((inputEl) => {
      const input = inputEl as HTMLInputElement;
      const container = input.closest('.template-field') as HTMLElement | null;
      const labelEl = container?.querySelector('label') as HTMLLabelElement | null;
      const titulo = String(labelEl?.textContent || '').replace('*', '').trim();
      const mappedType = titulo ? temporalTypeByTitle[titulo] : undefined;
      const temporalType = mappedType || (input.hasAttribute('data-bf-date') || input.hasAttribute('bf-date') ? 'DATE' : null);
      if (!temporalType) {
        return;
      }

      const previousType = (input as any).__cpaDateEnhancedType;
      if (previousType === temporalType) {
        return;
      }

      (input as any).__cpaDateEnhancedType = temporalType;
      if (temporalType === 'TIME' || temporalType === 'DATETIME') {
        // bf-date textual: mantém formato/serialização esperados pelo backend.
        input.type = 'text';
        input.style.display = '';
        return;
      }

      // Precisa ler/converter o valor ANTES de trocar type pra 'date': o browser zera
      // automaticamente valores não-ISO (ex: dd/mm/yyyy vindo da API) assim que o type muda.
      const valorOriginal = input.value;
      const isDateNative = /^\d{4}-\d{2}-\d{2}$/.test(valorOriginal);
      const valorConvertido = valorOriginal && !isDateNative
        ? parseTemporalValue(valorOriginal, temporalType)
        : null;

      input.type = 'date';
      input.removeAttribute('placeholder');
      input.removeAttribute('step');
      input.setAttribute('max', '9999-12-31');
      input.setAttribute('maxlength', '10');

      // Sincroniza valor existente para o formato nativo do input temporal.
      if (valorConvertido) {
        input.value = valorConvertido;
      }

      if (!(input as any).__cpaDateEnhanced) {
        input.addEventListener('input', () => {
          const val = input.value;
          if (!val) {
            return;
          }

          const yearExcessMatch = val.match(/^(\d{5,})-(\d{2})-(\d{2})$/);
          if (yearExcessMatch) {
            const clampedYear = yearExcessMatch[1].slice(0, 4);
            const corrected = `${clampedYear}-${yearExcessMatch[2]}-${yearExcessMatch[3]}`;
            input.value = corrected;

            const scope = this.angularJs.getScope();
            const ng = this.angularJs.getNg();
            if (scope && ng) {
              const ngModelCtrl = ng.element(input).controller('ngModel');
              if (ngModelCtrl) {
                scope.$apply(() => {
                  ngModelCtrl.$setViewValue(corrected);
                });
              }
            }
          }
        });

        input.addEventListener('change', () => {
          const scope = this.angularJs.getScope();
          const ng = this.angularJs.getNg();
          if (!scope || !ng) {
            return;
          }

          const ngModelCtrl = ng.element(input).controller('ngModel');
          if (ngModelCtrl) {
            scope.$apply(() => {
              ngModelCtrl.$setViewValue(input.value);
            });
          }
        });
        (input as any).__cpaDateEnhanced = true;
      }
    });
  }

  enhanceIntegerInputs(hostEl: HTMLElement | null | undefined): void {
    if (!hostEl) {
      return;
    }

    const integerInputs = hostEl.querySelectorAll('input[data-bf-restrict-number]');
    integerInputs.forEach((inputEl) => {
      const input = inputEl as HTMLInputElement;

      // Melhora UX em dispositivos móveis: abre teclado numérico.
      if (!input.hasAttribute('inputmode')) {
        input.setAttribute('inputmode', 'numeric');
      }

      const applyFilter = () => {
        const digitsOnly = String(input.value).replace(/\D/g, '');
        if (input.value !== digitsOnly) {
          input.value = digitsOnly;
          const scope = this.angularJs.getScope();
          const ng = this.angularJs.getNg();
          if (scope && ng) {
            const ngModelCtrl = ng.element(input).controller('ngModel');
            if (ngModelCtrl) {
              scope.$apply(() => {
                ngModelCtrl.$setViewValue(digitsOnly);
              });
            }
          }
        }
      };

      if (!(input as any).__cpaIntegerEnhanced) {
        input.addEventListener('keydown', (event: KeyboardEvent) => {
          const allowed = [
            'Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
            'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
            'Home', 'End'
          ];
          if (allowed.includes(event.key)) {
            return;
          }
          if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase())) {
            return;
          }
          if (!/^\d$/.test(event.key)) {
            event.preventDefault();
          }
        });
        input.addEventListener('input', applyFilter);
        input.addEventListener('blur', applyFilter);
        (input as any).__cpaIntegerEnhanced = true;
      }

      applyFilter();
    });
  }

  enhanceCpfCnpjInputs(
    hostEl: HTMLElement | null | undefined,
    maskTypeByTitle: Record<string, 'CPF' | 'CNPJ' | 'CPF_CNPJ'>
  ): void {
    if (!hostEl) {
      return;
    }

    const containers = hostEl.querySelectorAll('.template-field');
    containers.forEach((containerEl) => {
      const container = containerEl as HTMLElement;
      const labelEl = container.querySelector('label') as HTMLLabelElement | null;
      const inputEl = container.querySelector('input:not([type]), input[type="text"], input[type="tel"], input[type="number"]') as HTMLInputElement | null;
      if (!labelEl || !inputEl) {
        return;
      }

      const titulo = String(labelEl.textContent || '').replace('*', '').trim();
      const maskType = maskTypeByTitle[titulo];
      if (!maskType) {
        return;
      }

      const applyMask = () => {
        const digits = onlyDigits(inputEl.value);
        const maskedValue = applyDocumentMask(digits, maskType);
        if (inputEl.value !== maskedValue) {
          inputEl.value = maskedValue;
        }
      };

      if (!(inputEl as any).__cpaCpfCnpjEnhanced) {
        inputEl.addEventListener('input', applyMask);
        inputEl.addEventListener('blur', applyMask);
        (inputEl as any).__cpaCpfCnpjEnhanced = true;
      }

      applyMask();
    });
  }

  enhanceTemporalTextMasks(
    hostEl: HTMLElement | null | undefined,
    temporalTypeByTitle: Record<string, 'DATE' | 'TIME' | 'DATETIME'>,
    temporalFormatByTitle: Record<string, TemporalFieldFormat>
  ): void {
    if (!hostEl) {
      return;
    }

    const containers = hostEl.querySelectorAll('.template-field');
    containers.forEach((containerEl) => {
      const container = containerEl as HTMLElement;
      const labelEl = container.querySelector('label') as HTMLLabelElement | null;
      const inputEl = container.querySelector('input') as HTMLInputElement | null;
      if (!labelEl || !inputEl) {
        return;
      }

      const titulo = String(labelEl.textContent || '').replace('*', '').trim();
      const temporalType = temporalTypeByTitle[titulo];
      const temporalFormat = temporalFormatByTitle[titulo];
      if (temporalType !== 'TIME' && temporalType !== 'DATETIME') {
        return;
      }

      const applyMask = () => {
        const onlyNumbers = onlyDigits(inputEl.value);
        const masked = temporalType === 'TIME'
          ? formatTime(onlyNumbers, temporalFormat?.timePattern)
          : formatDateTime(onlyNumbers, temporalFormat?.datePattern, temporalFormat?.timePattern);
        if (masked !== inputEl.value) {
          inputEl.value = masked;
        }
      };

      if (!(inputEl as any).__cpaTemporalMaskBound) {
        inputEl.addEventListener('input', applyMask);
        inputEl.addEventListener('blur', applyMask);
        (inputEl as any).__cpaTemporalMaskBound = true;
      }

      applyMask();
    });
  }

  enhanceDecimalInputs(
    hostEl: HTMLElement | null | undefined,
    decimalFormatByTitle: Record<string, DecimalFieldFormat>
  ): void {
    if (!hostEl) {
      return;
    }

    const containers = hostEl.querySelectorAll('.template-field');
    containers.forEach((containerEl) => {
      const container = containerEl as HTMLElement;
      const labelEl = container.querySelector('label') as HTMLLabelElement | null;
      const inputEl = container.querySelector('input') as HTMLInputElement | null;
      if (!labelEl || !inputEl) {
        return;
      }

      const titulo = String(labelEl.textContent || '').replace('*', '').trim();
      const decimalConfig = decimalFormatByTitle[titulo];
      if (!decimalConfig) {
        return;
      }

      const applyMask = () => {
        const masked = formatDecimal(inputEl.value, decimalConfig, false);
        if (masked !== inputEl.value) {
          inputEl.value = masked;
        }
      };

      const applyBlurMask = () => {
        const masked = formatDecimal(inputEl.value, decimalConfig, true);
        if (masked !== inputEl.value) {
          inputEl.value = masked;
        }
      };

      if (!(inputEl as any).__cpaDecimalMaskBound) {
        inputEl.addEventListener('input', applyMask);
        inputEl.addEventListener('blur', applyBlurMask);
        (inputEl as any).__cpaDecimalMaskBound = true;
      }

      // applyMask (sem fill de zeros) na renderização inicial; zeros decimais só no blur.
      // Usar applyBlurMask aqui pré-preenchia "9.999.999,00" e bloqueava novos dígitos.
      applyMask();
    });
  }
}
